import { Marker } from "@react-google-maps/api";
import { AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import React, { useEffect, useState } from "react";
import geneva_rock_img from "../../../assets/images/maps/geneva_rock.png";
import pit_img from "../../../assets/images/maps/geneva_rock1.png";
import home_depot_img from "../../../assets/images/maps/home_depot.png";
import landfill_img from "../../../assets/images/maps/landfill.png";
import landfill_blue_img from "../../../assets/images/maps/landfill_blue.png";
import shell_img from "../../../assets/images/maps/shell.png";
import staker_parson_img from "../../../assets/images/maps/staker_parson.png";
import { fetchNearByServices } from "../../../services/mapsApiService";
import { IMarker } from "./types";
import { fetchUtilityLocations } from "../../../services/utilityLocationsApiService";
import { IUtilityLocation } from "../../settings/utility-locations/types";
import { distanceMeters } from "../../../common/utils";

interface IProps {
  markers: IMarker[];
  apiKey: string;
  mode: string;
}

const ServicesMarkers: React.FC<IProps> = (props) => {
  const map = useMap();
  const infoWindow: any = new google.maps.InfoWindow();
  let markers: { [key: number]: Marker } = {};
  const [services, setServices] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [utilityLocations, setUtilityLocations] = useState<IUtilityLocation[]>(
    [],
  );

  useEffect(() => {
    const fetchData = async () => {
      // Validate utility locations have valid coordinates
      const validated = await getUtilityLocations();
      setUtilityLocations(validated);

      // Then fetch services with utility locations available
      if (props.markers) {
        await getServicesLocations(props.markers, validated);
      }
    };
    fetchData();
  }, [props.markers]);

  async function getServicesLocations(
    markers: IMarker[] = [],
    utilityLocationsData: IUtilityLocation[] = [],
  ) {
    setIsLoading(true);
    const services = [];
    for (const marker of markers) {
      if (!marker?.position?.lat) continue;
      const resp = await fetchNearByServices({
        location: `${marker.position.lat},${marker.position.lng}`,
        radius: 10000,
        mode: props.mode,
      });

      if (!resp?.data) continue;

      for (const service of resp.data) {
        services.push({
          position: {
            lat: Number(service.location.lat),
            lng: Number(service.location.lng),
          },
          title: service.name,
          address: service.address,
          place_id: service.place_id,
          service_name: service.name,
        });
      }
    }
    console.log("formatted services", services);
    let uniqueServices = getUniquePlaces(services);

    // Filter out services that overlap with utility locations
    const filteredServices = uniqueServices.filter(
      (service) => !isServiceDuplicateOfUtility(service, utilityLocationsData),
    );
    // console.log("filteredServices:", filteredServices);
    setServices(filteredServices);
    setIsLoading(false);
  }

  function isServiceDuplicateOfUtility(
    service: any,
    uLocations: IUtilityLocation[],
  ): boolean {
    return uLocations.some((location) => {
      const d = distanceMeters(
        service.position.lat,
        service.position.lng,
        location.latitude,
        location.longitude,
      );
      return d < 80;
    });
  }

  async function getUtilityLocations(): Promise<IUtilityLocation[]> {
    // Fetch utility locations first
    const response = await fetchUtilityLocations({
      active_only: true,
    });

    const locations: IUtilityLocation[] = response?.data || [];
    return locations
      .filter(
        (location) =>
          location.latitude != null &&
          location.longitude != null &&
          !isNaN(location.latitude) &&
          !isNaN(location.longitude),
      )
      .map((location) => ({
        ...location,
        latitude: Number(location.latitude),
        longitude: Number(location.longitude),
      }));
  }

  function getUniquePlaces(services: any) {
    const uniquePlaceIds = new Set();
    const uniqueServices = [];

    for (const service of services) {
      if (!uniquePlaceIds.has(service.place_id)) {
        uniquePlaceIds.add(service.place_id);
        uniqueServices.push(service);
      }
    }

    return uniqueServices;
  }

  function handleMarkerClick(marker: any, index: number) {
    const directions = `https://www.google.com/maps/dir/Current+Location/${marker.position.lat},${marker.position.lng}`;
    infoWindow.setHeaderContent(marker.title);
    infoWindow.setContent(
      `<div style="font-weight:normal;">${marker.address}</div>
        <a style="font-size:small" target="_blank" href="${directions}">Get Directions</a>
        <br />`,
    );
    const anchor = markers[index];
    infoWindow.open(map, anchor);
  }

  function handleLocationClick(location: IUtilityLocation, index: number) {
    const directions = `https://www.google.com/maps/dir/Current+Location/${location.latitude},${location.longitude}`;
    infoWindow.setHeaderContent(location.name);
    infoWindow.setContent(
      `<div style="font-weight:normal;">${location.address}</div>
        <a style="font-size:small" target="_blank" href="${directions}">Get Directions</a>
        <br />`,
    );
    const anchor = markers[index];
    infoWindow.open(map, anchor);
  }

  const setMarkerRef = (marker: Marker | null, key: number) => {
    if ((marker && markers[key]) || (!marker && !markers[key])) return;

    if (marker) {
      markers[key] = marker;
    } else {
      delete markers[key];
    }
  };

  function getMarkerImage(service_name: string): string {
    const beachFlagImg =
      "https://developers.google.com/maps/documentation/javascript/examples/full/images/beachflag.png";

    if (!service_name) {
      return beachFlagImg;
    }
    service_name = service_name.toLowerCase();
    if (service_name.includes("shell")) {
      return shell_img;
    }
    if (service_name.includes("geneva")) {
      return geneva_rock_img;
    }
    if (service_name.includes("staker")) {
      return staker_parson_img;
    }
    if (service_name.includes("home depot")) {
      return home_depot_img;
    }
    if (service_name.includes("landfill")) {
      return landfill_img;
    }
    if (service_name.includes("christensen")) {
      return pit_img;
    }
    return beachFlagImg;
  }

  return (
    <>
      {isLoading && (
        <div
          className="position-fixed bg-white shadow-sm rounded px-3 py-2"
          style={{ top: "80px", right: "20px", zIndex: 1000 }}
        >
          <div className="d-flex align-items-center">
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <small>Loading services...</small>
          </div>
        </div>
      )}
      {services?.map((marker, index) => (
        <AdvancedMarker
          key={marker?.title + index}
          position={marker?.position}
          title={marker?.title}
          onClick={(e) => {
            handleMarkerClick(marker, index);
          }}
          ref={(m: any) => setMarkerRef(m, index)}
        >
          <img src={getMarkerImage(marker.service_name)} />
        </AdvancedMarker>
      ))}

      {utilityLocations?.map((location: IUtilityLocation, index) => (
        <AdvancedMarker
          key={location?.id}
          position={{
            lat: Number(location.latitude),
            lng: Number(location.longitude),
          }}
          title={location?.name}
          onClick={(e) => {
            handleLocationClick(location, index + services.length);
          }}
          ref={(m: any) => setMarkerRef(m, index + services.length)}
        >
          <img src={landfill_blue_img} />
        </AdvancedMarker>
      ))}
    </>
  );
};

export default ServicesMarkers;
